import { alpha } from '@mui/material/styles';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { filledInputClasses } from '@mui/material/FilledInput';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
// ----------------------------------------------------------------------
export function textField(theme) {
    const color = {
        focused: theme.palette.text.primary,
        active: theme.palette.text.secondary,
        placeholder: theme.palette.text.disabled
    };
    const font = {
        label: theme.typography.body2,
        value: theme.typography.body2
    };
    return {
        // HELPER
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginTop: theme.spacing(1)
                }
            }
        },
        // LABEL
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    ...font.label,
                    color: theme.palette.grey[700],
                    [`&.${inputLabelClasses.shrink}`]: {
                        ...font.label,
                        color: theme.palette.grey[700],
                        [`&.${inputLabelClasses.focused}`]: {
                            color: theme.palette.grey[700]
                        },
                        [`&.${inputLabelClasses.error}`]: {
                            color: theme.palette.error.main
                        },
                        [`&.${inputLabelClasses.disabled}`]: {
                            color: theme.palette.text.disabled
                        },
                        [`&.${inputLabelClasses.filled}`]: {
                            transform: 'translate<string>(12px, 6px) scale(0.75)'
                        }
                    }
                }
            }
        },
        // BASE
        MuiInputBase: {
            styleOverrides: {
                root: {
                    [`&.${inputBaseClasses.disabled}`]: {
                        '& svg': {
                            color: theme.palette.text.disabled
                        }
                    }
                },
                input: {
                    ...font.value,
                    '&::placeholder': {
                        opacity: 1,
                        color: color.placeholder
                    }
                }
            }
        },
        // STANDARD
        MuiInput: {
            styleOverrides: {
                underline: {
                    '&:before': {
                        borderBottomColor: alpha(theme.palette.grey[500], 0.32)
                    },
                    '&:after': {
                        borderBottomColor: color.focused
                    }
                }
            }
        },
        // OUTLINED
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    color: theme.palette.grey[700],
                    [`&.${outlinedInputClasses.focused}`]: {
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: color.focused
                        }
                    },
                    [`&.${outlinedInputClasses.error}`]: {
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: theme.palette.error.main
                        }
                    },
                    [`&.${outlinedInputClasses.disabled}`]: {
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: alpha(theme.palette.action.disabledBackground, 0.2),
                            borderStyle: 'dashed'
                        }
                    },
                    borderRadius: 12
                },
                notchedOutline: {
                    borderColor: alpha(theme.palette.grey[500], 0.2),
                    transition: theme.transitions.create(['border-color'], {
                        duration: theme.transitions.duration.shortest
                    })
                }
            }
        },
        // FILLED
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    paddingRight: 3,
                    backgroundColor: theme.palette.common.white92,
                    color: theme.palette.grey[700],
                    '&:hover': {
                        backgroundColor: theme.palette.common.white92
                    },
                    [`&.${filledInputClasses.focused}`]: {
                        backgroundColor: theme.palette.common.white92
                    },
                    [`&.${filledInputClasses.error}`]: {
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        [`&.${filledInputClasses.focused}`]: {
                            backgroundColor: alpha(theme.palette.error.main, 0.16)
                        }
                    },
                    [`&.${filledInputClasses.disabled}`]: {
                        backgroundColor: theme.palette.grey['500Transparent'][8],
                        borderStyle: 'dashaed'
                    }
                },
                underline: {
                    '&:before, &:after': {
                        borderBottom: 'none !important'
                    },
                    '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none !important'
                    },
                    '&.Mui-disabled:before': {
                        borderBottom: 'none !important'
                    },
                    '&.Mui-focused:after': {
                        borderBottom: 'none !important'
                    }
                },
                //Added this for Autofill
                input: {
                    ...font.value,
                    color: theme.palette.grey[700],
                    '&::placeholder': {
                        opacity: 1,
                        color: theme.palette.grey[700]
                    },
                    '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.common.white92} inset`,
                        WebkitTextFillColor: theme.palette.grey[700],
                        borderRadius: 12,
                        backgroundColor: theme.palette.common.white92,
                        transition: theme.transitions.create('background-color', {
                            duration: theme.transitions.duration.shortest
                        })
                    }
                }
            },
            variants: [
                {
                    props: { variant: 'grey8' },
                    style: {
                        borderRadius: 12,
                        backgroundColor: theme.palette.common.grey8,
                        color: theme.palette.grey[700],
                        '&:hover': {
                            backgroundColor: theme.palette.common.grey8
                        },
                        [`&.${filledInputClasses.focused}`]: {
                            backgroundColor: theme.palette.common.grey8
                        },
                        [`&.${filledInputClasses.error}`]: {
                            backgroundColor: alpha(theme.palette.error.main, 0.08),
                            [`&.${filledInputClasses.focused}`]: {
                                backgroundColor: alpha(theme.palette.error.main, 0.16)
                            }
                        },
                        [`&.${filledInputClasses.disabled}`]: {
                            backgroundColor: theme.palette.grey['500Transparent'][8],
                            borderStyle: 'dashed'
                        },
                        '&:before, &:after': {
                            display: 'none'
                        }
                    }
                }
            ]
        }
    };
}
